import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { CloudUpload, Close } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { vendorImageService } from '../../services/vendorImageService';
import { vendorProfileService } from '../../services/vendorProfileService';

interface ImageUploadPopupProps {
  open: boolean;
  onClose: () => void;
  onUpdate: (imageUrl: string) => void;
  vendorId: string;
  currentImageUrl?: string;
}

const ImageUploadPopup: React.FC<ImageUploadPopupProps> = ({
  open,
  onClose,
  onUpdate,
  vendorId,
  currentImageUrl
}) => {
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string>('');

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      setUploadedFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
    },
    maxFiles: 1,
    multiple: false
  });

  const removeFile = () => {
    setUploadedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  const handleUpdate = async () => {
    if (!uploadedFile) {
      toast.error('Please select an image to upload');
      return;
    }

    setIsSubmitting(true);
    setUploadProgress('Preparing upload...');

    try {
      // Step 1: Generate pre-signed upload URL
      setUploadProgress('Generating upload URL...');
      const urlData = await vendorImageService.generateUploadUrl({
        vendorId: vendorId,
        fileType: uploadedFile.type,
      });

      if (!urlData.success) {
        throw new Error('Failed to generate upload URL');
      }

      // Step 2: Upload to S3
      setUploadProgress('Uploading image...');
      await vendorImageService.uploadToS3(urlData.uploadUrl, uploadedFile);

      // Step 3: Confirm upload
      setUploadProgress('Confirming upload...');
      const confirmResponse = await vendorProfileService.editProfile(vendorId, {
        profileImageUrl: urlData.imageUrl,
      });

      if (confirmResponse.success) {
        toast.success('Profile image updated successfully!');
        onUpdate(urlData.imageUrl);
        handleClose();
      } else {
        throw new Error(confirmResponse.message || 'Failed to confirm upload');
      }
    } catch (error: any) {
      console.error('Error uploading image:', error);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.message || 
                          'Failed to upload image. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
      setUploadProgress('');
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setUploadedFile(null);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
      setUploadProgress('');
      onClose();
    }
  };

  const handleCancel = () => {
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        <Typography 
          component="span"
          variant="h6" 
          sx={{ 
          fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
          fontWeight: 700,
          fontSize: '20px',
          color: '#181818'
        }}>
          Update Profile Image
        </Typography>
        <IconButton onClick={handleClose} disabled={isSubmitting}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ py: 2 }}>
          {/* Upload Area */}
          <Box
            {...getRootProps()}
            sx={{
              border: '2px dashed',
              borderColor: isDragActive ? '#67314f' : '#c5cbd3',
              borderRadius: '12px',
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: isDragActive ? '#f6f8f8' : '#ffffff',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: '#67314f',
                backgroundColor: '#f6f8f8'
              }
            }}
          >
            <input {...getInputProps()} />
            <CloudUpload sx={{ fontSize: 48, color: '#67314f', mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1, color: '#181818', fontWeight: 600 }}>
              {isDragActive ? 'Drop the image here' : 'Drag & drop an image here'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#666' }}>
              or click to select a file
            </Typography>
            <Typography variant="caption" sx={{ color: '#999', mt: 1, display: 'block' }}>
              Supports: JPEG, PNG, GIF, WebP
            </Typography>
          </Box>

          {/* Preview */}
          {previewUrl && (
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ mb: 1, color: '#666' }}>
                Preview:
              </Typography>
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <Box
                  sx={{
                    width: 120,
                    height: 120,
                    backgroundImage: `url(${previewUrl})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    borderRadius: '12px',
                    border: '1px solid #e0e0e0'
                  }}
                />
                <IconButton
                  onClick={removeFile}
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    backgroundColor: '#ff4444',
                    color: 'white',
                    width: 24,
                    height: 24,
                    '&:hover': {
                      backgroundColor: '#cc0000'
                    }
                  }}
                >
                  <Close sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Box>
          )}

          {/* Progress */}
          {isSubmitting && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" sx={{ mb: 1, color: '#666' }}>
                {uploadProgress}
              </Typography>
              <LinearProgress sx={{ borderRadius: '4px' }} />
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            width: '100%'
          }}
        >
          {/* Update Button */}
          <Button
            onClick={handleUpdate}
            disabled={!uploadedFile || isSubmitting}
            fullWidth
            sx={{
              height: '56px',
              backgroundColor: '#67314f',
              borderRadius: '16px',
              fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
              fontWeight: 700,
              fontSize: '16px',
              letterSpacing: '-0.32px',
              textTransform: 'none',
              color: 'white',
              '&:hover': {
                backgroundColor: '#5a2a42'
              },
              '&:disabled': {
                backgroundColor: '#93989c',
                color: 'white'
              }
            }}
          >
            {isSubmitting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} sx={{ color: 'white' }} />
                <Typography sx={{ fontSize: '14px' }}>{uploadProgress}</Typography>
              </Box>
            ) : (
              'Update Image'
            )}
          </Button>

          {/* Cancel Button */}
          <Button
            onClick={handleCancel}
            disabled={isSubmitting}
            fullWidth
            sx={{
              height: '56px',
              backgroundColor: 'transparent',
              borderRadius: '16px',
              fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
              fontWeight: 700,
              fontSize: '16px',
              letterSpacing: '-0.32px',
              textTransform: 'none',
              color: '#67314f',
              '&:hover': {
                backgroundColor: 'rgba(103, 49, 79, 0.04)'
              }
            }}
          >
            Cancel
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ImageUploadPopup;
